import { TIPS, TIPS_DEFAULT_LANG } from '../functions/weighted-tips.utils'
import { AppLanguage } from '../enums/app-language.enum'

import { DeviceDetectorService } from './device-detector.service'

const useTips = (detect: DeviceDetectorService) => {
	if (detect.isMobile()) return {
		// no tips on mobile devices 
		// there is a default message only visible on small screens instead
		often: []
	}

	return {
		often: [
			{
				fr: "Activez les relances automatiques pour éviter les oublis et maximiser vos chances d'encaissement.",
				en: "Activate automatic reminders to avoid oversights and maximize your chances of collection.",
			},
			{
				fr: "Personnalisez vos scénarios de relance selon le profil client pour un recouvrement plus efficace.",
				en: "Customize your reminder scenarios according to the client profile for more effective recovery.",
			},
			{
				fr: "Surveillez votre DSO (Days Sales Outstanding) via le tableau de bord pour optimiser votre trésorerie.",
				en: "Monitor your DSO (Days Sales Outstanding) via the dashboard to optimize your cash flow.",
			},
			{
				fr: "Planifiez vos actions de relance directement depuis l'interface pour un suivi organisé.",
				en: "Plan your reminder actions directly from the interface for organized tracking.",
			},
			{
				fr: "Utilisez les modèles de relance optimisés pour améliorer le taux de réponse de vos clients.",
				en: "Use optimized reminder templates to improve your clients' response rate.",
			},
			{
				fr: "Synchronisez votre ERP avec Aston Cash Collection pour garder vos données à jour.",
				en: "Synchronize your ERP with Aston Cash Collection to keep your data up to date.",
			},
			{
				fr: "Personnalisez vos emails de relance pour un impact plus fort auprès de vos clients.",
				en: "Customize your reminder emails for a stronger impact on your clients.",
			},
			{
				fr: "Consultez les prévisions de cashflow pour anticiper vos besoins de trésorerie.",
				en: "Consult cash flow forecasts to anticipate your cash needs.",
			},
			{
				fr: "Utilisez la fonction de relance multi-canal (email, SMS, courrier) pour toucher vos clients efficacement.",
				en: "Use the multi-channel reminder function (email, SMS, mail) to effectively reach your clients.",
			},
			{
				fr: "Utilisez le scoring client pour identifier rapidement les clients à risque.",
				en: "Use client scoring to quickly identify at-risk clients.",
			},
			{
				fr: "Exportez vos relances envoyées pour prouver vos démarches en cas de litige.",
				en: "Export your sent reminders to prove your actions in case of a dispute.",
			},
			{
				fr: "Définissez une relance progressive pour adapter le ton du message en fonction du retard.",
				en: "Set up a progressive reminder to adjust the tone of the message based on the delay.",
			},
			{
				fr: "Paramétrez des relances automatiques sur les créances récurrentes pour un gain de temps.",
				en: "Set up automatic reminders on recurring debts to save time.",
			},
			{
				fr: "Analysez vos encaissements par période pour détecter les tendances de paiement.",
				en: "Analyze your collections by period to detect payment trends.",
			},
			{
				fr: "Relancez vos clients avant l’échéance pour éviter les retards de paiement.",
				en: "Remind your clients before the due date to avoid payment delays.",
			},
			{
				fr: "Améliorez l’efficacité des relances en intégrant un lien de paiement dans vos emails.",
				en: "Improve the effectiveness of reminders by including a payment link in your emails.",
			},
		],
		rarely: [
			{
				fr: "Utilisez les filtres pour trier rapidement vos pièces comptables par date d'échéance, montant ou niveau de relance.",
				en: "Use filters to quickly sort your accounting items by due date, amount, or reminder level.",
			},
			{
				fr: "Ajoutez des commentaires aux fiches client pour un suivi précis et une meilleure collaboration.",
				en: "Add comments to client records for precise tracking and better collaboration.",
			},
			{
				fr: "Exportez vos données en Excel pour une analyse approfondie et un reporting sur mesure.",
				en: "Export your data to Excel for in-depth analysis and customized reporting.",
			},
			{
				fr: "Consultez l'historique des interactions avec chaque client pour une meilleure prise de décision.",
				en: "Consult the interaction history with each client for better decision-making.",
			},
			{
				fr: "Activez les notifications pour être alerté des paiements entrants et des relances urgentes.",
				en: "Activate notifications to be alerted of incoming payments and urgent reminders.",
			},
			{
				fr: "Vérifiez les montants en retard par client pour prioriser vos actions de recouvrement.",
				en: "Check overdue amounts by client to prioritize your collection actions.",
			},
			{
				fr: "Assignez des tâches à vos collègues pour une gestion collaborative des créances.",
				en: "Assign tasks to your colleagues for collaborative debt management.",
			},
			{
				fr: "Configurez les seuils de risque pour détecter rapidement les clients à surveiller.",
				en: "Set risk thresholds to quickly detect clients to monitor.",
			},
			{
				fr: "Analysez vos performances de recouvrement avec les KPIs disponibles dans le tableau de bord.",
				en: "Analyze your collection performance with the KPIs available in the dashboard.",
			},
			{
				fr: "Ajoutez des notes internes aux fiches clients pour conserver un historique clair des échanges.",
				en: "Add internal notes to client records to maintain a clear history of exchanges.",
			},
			{
				fr: "Paramétrez des rappels pour ne jamais manquer une échéance critique.",
				en: "Set reminders to never miss a critical deadline.",
			},
			{
				fr: "Suivez l’évolution de vos créances avec les graphiques disponibles au sein des tableaux de bord.",
				en: "Track the evolution of your receivables with the charts available in the dashboards.",
			},
			{
				fr: "Centralisez toutes les informations de recouvrement pour une vision complète sur chaque dossier.",
				en: "Centralize all collection information for a complete view of each case.",
			},
			{
				fr: "Testez différentes stratégies de relance pour trouver celles qui fonctionnent le mieux.",
				en: "Test different reminder strategies to find the ones that work best.",
			},
			{
				fr: "Évitez les litiges en documentant chaque échange avec le client dans la plateforme.",
				en: "Avoid disputes by documenting each exchange with the client in the platform.",
			},
			{
				fr: "Assurez-vous que vos données clients sont bien à jour pour éviter les erreurs de relance.",
				en: "Ensure your client data is up to date to avoid reminder errors.",
			},
			{
				fr: "Optimisez votre communication avec des relances adaptées à chaque typologie de client.",
				en: "Optimize your communication with reminders tailored to each client type.",
			},
			{
				fr: "Vérifiez les créances en contentieux pour suivre leur avancement et ajuster vos actions.",
				en: "Check disputed debts to track their progress and adjust your actions.",
			},
			{
				fr: "Mettez en place des relances différenciées selon le montant de la créance.",
				en: "Set up differentiated reminders based on the amount of the debt.",
			},
			{
				fr: "Anticipez les problèmes de trésorerie avec un suivi rigoureux des encaissements attendus.",
				en: "Anticipate cash flow problems with rigorous tracking of expected collections.",
			},
			{
				fr: "Utilisez les alertes pour repérer rapidement la dégradation de la situation de certains créanciers",
				en: "Use alerts to quickly spot the deterioration of some creditors' situations.",
			},
			{
				fr: "Centralisez vos relances et interactions avec chaque client pour éviter les doublons.",
				en: "Centralize your reminders and interactions with each client to avoid duplicates.",
			},
			{
				fr: "Affinez vos actions en analysant les délais moyens de paiement de vos clients.",
				en: "Refine your actions by analyzing the average payment times of your clients.",
			},
			{
				fr: "Taggez vos collaborateurs dans vos commentaires afin qu'ils soient notifiés.",
				en: "Tag your colleagues in your comments so they are notified.",
			},
		]
	}
}

export function provideTips() {
	return [
		{ provide: TIPS, useFactory: useTips, deps: [DeviceDetectorService] },
		{ provide: TIPS_DEFAULT_LANG, useValue: AppLanguage.FR },
	]
}
